import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import Alert from "@mui/material/Alert"; // Import Alert for Snackbar
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, statusDetail, onRecordUpdated }) => {
  // Renamed termDetail to statusDetail
  const { updateData, deleteData } = useFirestore("ShellRequestStatus"); // Specify collection
  const [formData, setFormData] = useState({
    id: "", // To store Firestore document ID
    Status: "",
    Active: 0,
    Approval_Page_Option: 0,
    Dropdown_Option: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (statusDetail) {
      console.log("ShellStatus Edit - statusDetail received:", statusDetail);
      console.log(
        "ShellStatus Edit - Active value:",
        statusDetail.Active,
        "Type:",
        typeof statusDetail.Active,
      );
      console.log(
        "ShellStatus Edit - Approval_Page_Option value:",
        statusDetail.Approval_Page_Option,
        "Type:",
        typeof statusDetail.Approval_Page_Option,
      );

      // Helper function to normalize boolean values for string-based Active field
      const normalizeActiveBoolean = (value) => {
        if (
          value === "Yes" ||
          value === -1 ||
          value === "-1" ||
          value === true ||
          value === "true" ||
          value === 1 ||
          value === "1"
        ) {
          return "Yes";
        }
        return "No";
      };

      // Helper function for Approval_Page_Option (still uses -1/0)
      const normalizeApprovalBoolean = (value) => {
        if (
          value === -1 ||
          value === "-1" ||
          value === true ||
          value === "true" ||
          value === 1 ||
          value === "1"
        ) {
          return -1;
        }
        return 0;
      };

      const normalizedActive = normalizeActiveBoolean(statusDetail.Active);
      const normalizedApproval = normalizeApprovalBoolean(
        statusDetail.Approval_Page_Option,
      );

      setFormData({
        id: statusDetail.id || "",
        Status: statusDetail.Status || "",
        Active: normalizedActive,
        Approval_Page_Option: normalizedApproval,
        Dropdown_Option: statusDetail.Dropdown_Option || "",
      });

      console.log("ShellStatus Edit - formData set to:", {
        Active: normalizedActive,
        Approval_Page_Option: normalizedApproval,
        ActiveCheckboxWillBe: normalizedActive === "Yes",
        ApprovalCheckboxWillBe: normalizedApproval === -1,
      });
    } else {
      // Reset form if statusDetail is null
      setFormData({
        id: "",
        Status: "",
        Active: "No",
        Approval_Page_Option: 0,
        Dropdown_Option: "",
      });
    }
  }, [statusDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === "Active") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: checked ? "Yes" : "No",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: checked ? -1 : 0,
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing for update.");
      }
      await updateData(formData.id, formData); // Pass ID and data separately
      setLoading(false);
      setSnackbarMessage("Record has been updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close dialog on success
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!statusDetail || !statusDetail.id) {
        throw new Error("Document ID is missing for delete.");
      }
      await deleteData(statusDetail.id);
      setLoading(false);
      setSnackbarMessage("Record has been deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close the main dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Shell Status
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Status"
                name="Status"
                value={formData.Status}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active === "Yes"}
                    onChange={handleCheckboxChange}
                    name="Active"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Approval_Page_Option === -1}
                    onChange={handleCheckboxChange}
                    name="Approval_Page_Option"
                  />
                }
                label="Approval Page Option"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Dropdown Option"
                name="Dropdown_Option"
                value={formData.Dropdown_Option}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
